import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import {
  useAppSettingsData,
  useAppUsersData,
  useUpdateAppSettings,
  type AppBillingView,
  type AppDataModePreference,
  type AppLandingRoute,
  type AppUserRecord,
} from '../data/settingsApi.ts';
import { displayNameLabel, formatDateTimeLabel, roleLabel } from '../utils';

export function SettingsPage(props: {
  apiEnabled: boolean;
  authBypassed: boolean;
  currentUserName: string | null;
  currentUserRole: string | null;
  configuredMode: 'auto' | 'local' | 'http';
  resolvedMode: 'local' | 'http' | null;
  onLogout: () => Promise<void>;
}) {
  const canViewUsers = props.currentUserRole === 'admin';
  const { users, isLoading, error } = useAppUsersData(props.apiEnabled && canViewUsers);
  const { settings, isLoading: settingsLoading, error: settingsError } = useAppSettingsData(props.apiEnabled);
  const updateSettingsMutation = useUpdateAppSettings();
  const [landingRoute, setLandingRoute] = useState<AppLandingRoute>('overview');
  const [billingView, setBillingView] = useState<AppBillingView>('queue');
  const [mobileAutoLoginUserId, setMobileAutoLoginUserId] = useState<string>('');
  const [preferredDataMode, setPreferredDataMode] = useState<AppDataModePreference>('auto');
  const [enableOfflineCache, setEnableOfflineCache] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const activeUsers = canViewUsers ? users.filter((user) => user.active) : [];
  const usersByRole = activeUsers.reduce<Record<string, number>>((acc, user) => {
    acc[user.role] = (acc[user.role] ?? 0) + 1;
    return acc;
  }, {});
  const technicianUsers = activeUsers.filter((user) => user.role === 'technician');
  const selectedUser = activeUsers.find((user) => user.id === selectedUserId) ?? null;

  useEffect(() => {
    if (!settings?.preferences) return;
    setLandingRoute(settings.preferences.landingRoute);
    setBillingView(settings.preferences.billingView);
    setMobileAutoLoginUserId(settings.preferences.mobileAutoLoginUserId ?? '');
    setPreferredDataMode(settings.preferences.preferredDataMode);
    setEnableOfflineCache(settings.preferences.enableOfflineCache);
  }, [settings?.preferences]);

  useEffect(() => {
    if (selectedRole || !Object.keys(usersByRole).length) return;
    setSelectedRole(Object.keys(usersByRole)[0] ?? null);
  }, [selectedRole, usersByRole]);

  const isPreferencesDirty = Boolean(
    settings?.preferences &&
      (
        settings.preferences.landingRoute !== landingRoute ||
        settings.preferences.billingView !== billingView ||
        (settings.preferences.mobileAutoLoginUserId ?? '') !== mobileAutoLoginUserId ||
        settings.preferences.preferredDataMode !== preferredDataMode ||
        settings.preferences.enableOfflineCache !== enableOfflineCache
      ),
  );

  async function handleSavePreferences() {
    await updateSettingsMutation.mutateAsync({
      landingRoute,
      billingView,
      mobileAutoLoginUserId: mobileAutoLoginUserId || null,
      preferredDataMode,
      enableOfflineCache,
    });
    setSaveNotice('Préférences enregistrées.');
    window.setTimeout(() => setSaveNotice(null), 3500);
  }

  return (
    <div className="page-stack">
      <div className="two-column-layout">
        <Card title="Session courante" subtitle="État d'accès et mode de fonctionnement actif.">
          <div className="detail-grid detail-grid--summary settings-summary-grid">
            <div className="detail-card">
              <span className="detail-card__label">Utilisateur</span>
              <strong>{displayNameLabel(props.currentUserName) ?? 'Aucun'}</strong>
              <span>{roleLabel(props.currentUserRole)}</span>
            </div>
            <div className="detail-card">
              <span className="detail-card__label">Auth</span>
              <strong>{props.authBypassed ? 'Contournement local' : 'Session active'}</strong>
              <span>{props.authBypassed ? "L'API d'authentification est contournée" : 'Cookie et jeton de session pris en charge'}</span>
            </div>
            <div className="detail-card">
              <span className="detail-card__label">Mode données</span>
              <strong>{props.resolvedMode ?? props.configuredMode}</strong>
              <span>Configuré: {props.configuredMode}</span>
            </div>
            <div className="detail-card">
              <span className="detail-card__label">Espace applicatif</span>
              <strong>{props.apiEnabled ? 'Actif' : 'Désactivé'}</strong>
              <span>{props.apiEnabled ? 'Parcours applicatifs reliés' : 'Repli local uniquement'}</span>
            </div>
          </div>

          {settings?.capabilities ? (
            <div className="stack-list">
              <div className="signal-row">
                <div>
                  <strong>Surface MVP</strong>
                  <span>{settings.capabilities.protectedMvp ? 'Protégée par session' : 'Ouverte'}</span>
                </div>
                <div className="signal-row__value">{settings.capabilities.protectedMvp ? 'ON' : 'OFF'}</div>
              </div>
              <div className="signal-row">
                <div>
                  <strong>Pipeline de facturation</strong>
                  <span>{settings.capabilities.billingPipeline ? 'Dossiers + MVP consolidés' : 'Non disponible'}</span>
                </div>
                <div className="signal-row__value">{settings.capabilities.billingPipeline ? 'API' : 'N/D'}</div>
              </div>
            </div>
          ) : null}

          {!props.authBypassed ? (
            <div className="action-row">
              <button type="button" className="secondary-button" onClick={() => void props.onLogout()}>
                Fermer la session
              </button>
            </div>
          ) : null}
        </Card>

        <Card title="Préférences persistées" subtitle="Paramètres appliqués par utilisateur, sauvegardés côté backend.">
          {props.apiEnabled ? (
            <>
              {settingsLoading ? (
                <div className="app-empty">
                  <strong>Chargement</strong>
                  <span>Lecture des préférences en cours.</span>
                </div>
              ) : null}

              {settingsError ? (
                <div className="alert-card alert-card--danger">
                  <strong>Lecture préférences impossible</strong>
                  <span>{settingsError instanceof Error ? settingsError.message : 'Erreur inconnue.'}</span>
                </div>
              ) : null}

              {settings ? (
                <>
                  <div className="settings-form-grid">
                    <label className="field-stack">
                      <span>Route d'ouverture</span>
                      <select value={landingRoute} onChange={(event) => setLandingRoute(event.target.value as typeof landingRoute)}>
                        <option value="overview">Vue d'ensemble</option>
                        <option value="dispatch">Planification</option>
                        <option value="work-orders">Interventions</option>
                        <option value="quotes">Devis</option>
                        <option value="billing">Facturation</option>
                        <option value="settings">Paramètres</option>
                        <option value="mobile">Mobile terrain</option>
                      </select>
                    </label>

                    <label className="field-stack">
                      <span>Vue facturation par défaut</span>
                      <select value={billingView} onChange={(event) => setBillingView(event.target.value as typeof billingView)}>
                        <option value="queue">File active</option>
                        <option value="ready">Prêts à facturer</option>
                        <option value="blocked">Bloqués</option>
                        <option value="invoiced">Facturés</option>
                        <option value="all">Tout voir</option>
                      </select>
                    </label>

                    <label className="field-stack">
                      <span>Mode données préféré</span>
                      <select value={preferredDataMode} onChange={(event) => setPreferredDataMode(event.target.value as typeof preferredDataMode)}>
                        <option value="auto">Auto</option>
                        <option value="http">API HTTP</option>
                        <option value="local">Local uniquement</option>
                      </select>
                    </label>

                    <label className="field-stack">
                      <span>Connexion auto mobile</span>
                      <select value={mobileAutoLoginUserId} onChange={(event) => setMobileAutoLoginUserId(event.target.value)}>
                        <option value="">Aucun</option>
                        {technicianUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.displayName}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="settings-toggle">
                    <input
                      type="checkbox"
                      checked={enableOfflineCache}
                      onChange={(event) => setEnableOfflineCache(event.target.checked)}
                    />
                    <span>Activer le cache hors ligne mobile par défaut</span>
                  </label>

                  {updateSettingsMutation.error ? (
                    <div className="alert-card alert-card--danger">
                      <strong>Sauvegarde impossible</strong>
                      <span>{updateSettingsMutation.error instanceof Error ? updateSettingsMutation.error.message : 'Erreur inconnue.'}</span>
                    </div>
                  ) : null}
                  {saveNotice ? (
                    <div className="alert-card alert-card--success">
                      <strong>Enregistré</strong>
                      <span>{saveNotice}</span>
                    </div>
                  ) : null}

                  <div className="action-row">
                    <button
                      type="button"
                      className="primary-button"
                      onClick={() => void handleSavePreferences()}
                      disabled={!isPreferencesDirty || updateSettingsMutation.isPending}
                    >
                      {updateSettingsMutation.isPending ? 'Sauvegarde...' : 'Enregistrer'}
                    </button>
                    <span className="settings-updated-at">
                      Dernière mise à jour: {formatDateTimeLabel(settings.preferences.updatedAt)}
                    </span>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <div className="alert-card alert-card--warning">
              <strong>Préférences API indisponibles</strong>
              <span>Le mode local contourne l'API de paramètres ; les préférences persistées ne s'appliquent qu'en mode HTTP.</span>
            </div>
          )}
        </Card>
      </div>

      <Card title="Répartition des rôles" subtitle="Vue rapide du référentiel d'utilisateurs seedés.">
        {canViewUsers ? (
          <div className="stack-list">
            {Object.entries(usersByRole).length ? (
              Object.entries(usersByRole).map(([role, count]) => (
                <button
                  key={role}
                  type="button"
                  className={`signal-row signal-row--interactive${selectedRole === role ? ' is-active' : ''}`}
                  onClick={() => setSelectedRole(role)}
                >
                  <div>
                    <strong>{roleLabel(role)}</strong>
                    <span>Utilisateurs actifs</span>
                  </div>
                  <div className="signal-row__value">{count}</div>
                </button>
              ))
            ) : (
              <div className="app-empty">
                <strong>Aucun rôle chargé</strong>
                <span>La liste apparaîtra dès que `/api/app/users` répondra via session active.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="alert-card alert-card--info">
            <strong>Accès restreint</strong>
            <span>Le référentiel détaillé des rôles est réservé à l'administrateur.</span>
          </div>
        )}
      </Card>

      <Card title="Annuaire d'utilisateurs" subtitle="Utilisateurs exposés par le backend app-domain.">
        {isLoading ? (
          <div className="app-empty">
            <strong>Chargement</strong>
            <span>Lecture des utilisateurs en cours.</span>
          </div>
        ) : null}

        {canViewUsers && error ? (
          <div className="alert-card alert-card--danger">
            <strong>Lecture utilisateurs impossible</strong>
            <span>{error instanceof Error ? error.message : 'Erreur inconnue.'}</span>
          </div>
        ) : null}

        <div className="user-directory">
          {canViewUsers ? users.map((user: AppUserRecord) => (
            <button
              key={user.id}
              type="button"
              className={`user-directory__item user-directory__item--interactive${selectedUserId === user.id ? ' is-active' : ''}`}
              onClick={() => setSelectedUserId(user.id)}
            >
              <div className="user-directory__head">
                <strong>{displayNameLabel(user.displayName)}</strong>
                <span className={`status-pill ${user.active ? 'status-pill--ready' : 'status-pill--danger'}`}>
                  {user.active ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <span>{user.email}</span>
              <div className="user-directory__meta">
                <span>{roleLabel(user.role)}</span>
                <span>{formatDateTimeLabel(user.createdAt)}</span>
              </div>
            </button>
          )) : (
            <div className="app-empty">
              <strong>Annuaire masqué</strong>
              <span>Changer de rôle pour gérer les utilisateurs.</span>
            </div>
          )}
        </div>

        {selectedUser ? (
          <div className="settings-user-detail">
            <strong>{displayNameLabel(selectedUser.displayName)}</strong>
            <span>{selectedUser.email}</span>
            <span>{roleLabel(selectedUser.role)} · {selectedUser.active ? 'Actif' : 'Inactif'}</span>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
